import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Globe, LogOut, LayoutDashboard, BarChart3, Bell, Radio, Wallet, Library } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE as API } from '../../lib/apiBase';


export function Header() {
  const { t, lang, toggleLang } = useLang();
  const { user, token, logout, isAdmin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    const fetchNotifs = () => {
      fetch(`${API}/api/notifications?limit=10`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { setUnread(d.unread_count || 0); setNotifs(d.notifications || []); }).catch(() => {});
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 15000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    setMenuOpen(false);
    setNotifOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    setUnread(0);
    setNotifs(n => n.map(x => ({ ...x, read: true })));
  };

  if (location.pathname.startsWith('/dashboard')) return null;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between h-14 px-4 gap-2">
        <div className="flex items-center gap-3">
          <button
            data-testid="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/5 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/assets/logo.png" alt="PulseMusic" className="w-8 h-8 object-contain" style={{background:'transparent'}} />
            <span className="font-heading font-bold text-lg tracking-tight hidden sm:block">
              PulseMusic
            </span>
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to="/" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200">{t('nav.feed')}</Link>
          <Link to="/events" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200">{t('nav.map')}</Link>
          <Link to="/live" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200 flex items-center gap-1">
            <Radio size={12} className="text-red-500" /> {lang === 'es' ? 'En Vivo' : 'Live'}
          </Link>
          <Link to="/playlists" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200">{t('nav.playlists')}</Link>
          <Link to="/library" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200 flex items-center gap-1">
            <Library size={12} /> {lang === 'es' ? 'Colección' : 'Library'}
          </Link>
          <Link
            to="/producer"
            className="text-sm font-semibold transition-all duration-200 flex items-center gap-1.5 px-3 py-1 rounded-full border"
            style={{ color: '#F5C842', borderColor: 'rgba(245,200,66,0.4)', background: 'rgba(245,200,66,0.08)' }}
          >
            <span style={{ fontSize: 11 }}>🎵</span> Producer
          </Link>
          <Link to="/support" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200">{t('nav.support')}</Link>
          {isAdmin && (
            <>
              <Link to="/dashboard" className="text-sm text-pulse-primary hover:text-pulse-primary-hover transition-colors duration-200 flex items-center gap-1">
                <LayoutDashboard size={14} /> {t('dashboard.title')}
              </Link>
              <Link to="/analytics" className="text-sm text-pulse-muted hover:text-white transition-colors duration-200 flex items-center gap-1">
                <BarChart3 size={14} /> {t('analytics.title')}
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          {!searchOpen && (
            <button
              data-testid="search-toggle"
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors duration-200"
              aria-label={t('common.search')}
            >
              <Search size={18} className="text-pulse-muted" />
            </button>
          )}

          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors duration-200 flex items-center gap-1"
            aria-label={t('common.language')}
          >
            <Globe size={16} className="text-pulse-muted" />
            <span className="text-xs text-pulse-muted uppercase">{lang}</span>
          </button>

          {user ? (
            <div className="flex items-center gap-1 sm:gap-2">
              <Link to="/wallet" data-testid="wallet-link" className="p-2 rounded-lg hover:bg-white/5 transition-colors duration-200 relative">
                <Wallet size={16} className="text-pulse-muted" />
              </Link>
              <div className="relative">
                <button
                  data-testid="notif-bell"
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors duration-200 relative"
                >
                  <Bell size={16} className="text-pulse-muted" />
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>}
                </button>
                {notifOpen && (
                  <div data-testid="notif-dropdown" className="absolute right-0 top-10 w-80 bg-pulse-surface border border-pulse-border rounded-xl shadow-2xl z-50 max-h-96 overflow-y-auto">
                    <div className="flex items-center justify-between p-3 border-b border-pulse-border">
                      <span className="text-sm font-heading font-bold">{lang === 'es' ? 'Notificaciones' : 'Notifications'}</span>
                      {unread > 0 && <button onClick={markAllRead} className="text-[10px] text-pulse-primary">{lang === 'es' ? 'Marcar todo leido' : 'Mark all read'}</button>}
                    </div>
                    {notifs.length === 0 ? (
                      <p className="p-4 text-xs text-pulse-muted text-center">{lang === 'es' ? 'Sin notificaciones' : 'No notifications'}</p>
                    ) : notifs.map(n => (
                      <div key={n.id} className={`p-3 border-b border-pulse-border/50 text-xs ${!n.read ? 'bg-cyan-500/5' : ''}`}>
                        <p className="font-semibold">{n.title}</p>
                        <p className="text-pulse-muted mt-0.5">{n.message}</p>
                        <p className="text-[9px] text-pulse-muted mt-1">{new Date(n.created_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/profile"
                data-testid="user-profile-link"
                className="text-sm text-pulse-muted hover:text-white transition-colors duration-200 hidden md:block"
              >
                {user.name}
              </Link>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors duration-200"
                aria-label={t('auth.logout')}
              >
                <LogOut size={16} className="text-pulse-muted" />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="login-link"
              className="text-sm bg-pulse-primary text-black px-4 py-1.5 rounded-full font-semibold hover:bg-pulse-primary-hover transition-colors duration-200"
            >
              {t('auth.login')}
            </Link>
          )}
        </div>
      </div>

      {searchOpen && (
        <div data-testid="header-search-panel" className="absolute left-0 right-0 top-14 border-t border-pulse-border bg-pulse-surface/95 backdrop-blur-xl px-4 py-3 shadow-2xl">
          <form onSubmit={handleSearch} className="max-w-7xl mx-auto flex items-center gap-2">
            <input
              data-testid="search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('common.search')}
              className="flex-1 bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              autoFocus
            />
            <button type="button" data-testid="close-search-panel" onClick={() => setSearchOpen(false)} className="p-2 rounded-lg hover:bg-white/5 transition-colors duration-200">
              <X size={16} />
            </button>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div data-testid="mobile-menu-panel" className="md:hidden bg-pulse-surface border-t border-pulse-border animate-fade-in-up">
          <div className="flex flex-col p-4 gap-3">
            <Link to="/" onClick={() => setMenuOpen(false)} className="text-sm py-2">{t('nav.feed')}</Link>
            <Link to="/events" onClick={() => setMenuOpen(false)} className="text-sm py-2">{t('nav.map')}</Link>
            <Link to="/live" onClick={() => setMenuOpen(false)} className="text-sm py-2 flex items-center gap-2"><Radio size={12} className="text-red-500" /> {lang === 'es' ? 'En Vivo' : 'Live'}</Link>
            <Link to="/playlists" onClick={() => setMenuOpen(false)} className="text-sm py-2">{t('nav.playlists')}</Link>
            <Link to="/library" onClick={() => setMenuOpen(false)} className="text-sm py-2 flex items-center gap-2"><Library size={12} className="text-pulse-primary" /> {lang === 'es' ? 'Colección' : 'Library'}</Link>
            <Link to="/wallet" onClick={() => setMenuOpen(false)} className="text-sm py-2 flex items-center gap-2"><Wallet size={12} className="text-pulse-primary" /> {lang === 'es' ? 'Monedero' : 'Wallet'}</Link>
            <Link to="/support" onClick={() => setMenuOpen(false)} className="text-sm py-2">{t('nav.support')}</Link>
            {isAdmin && (
              <>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="text-sm py-2 text-pulse-primary">{t('dashboard.title')}</Link>
                <Link to="/analytics" onClick={() => setMenuOpen(false)} className="text-sm py-2">{t('analytics.title')}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
